import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from 'brd-react-components';
import type { UseEmblaCarouselType } from 'embla-carousel-react';
import type { ComponentType } from '@/components/region';
import { TickerSlide } from '@/extensions/ticker/components/ticker-slide';

type CarouselApi = UseEmblaCarouselType[1];

type TickerCarouselProps = {
    components: ComponentType[];
};

export function TickerCarousel({ components }: TickerCarouselProps): ReactElement | null {
    const slideCount = components.length;
    const isSingleSlide = slideCount === 1;
    const [api, setApi] = useState<CarouselApi | null>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    const onSelect = useCallback(() => {
        if (!api) {
            return;
        }
        setSelectedIndex(api.selectedScrollSnap());
        setIsPaused(false);
    }, [api]);

    useEffect(() => {
        if (!api) {
            return;
        }

        onSelect();
        api.on('select', onSelect);
        api.on('reInit', onSelect);

        return () => {
            api.off('select', onSelect);
            api.off('reInit', onSelect);
        };
    }, [api, onSelect]);

    const handleCycleComplete = useCallback(() => {
        if (!isSingleSlide) {
            api?.scrollNext();
        }
    }, [api, isSingleSlide]);

    if (slideCount === 0) {
        return null;
    }

    return (
        <aside className="ticker-shell">
            <div className="ticker-container">
                {isSingleSlide ? (
                    <TickerSlide
                        component={components[0]}
                        isActive
                        isPaused={isPaused}
                        isSingleSlide
                        onMarqueePause={() => setIsPaused(true)}
                        onMarqueeResume={() => setIsPaused(false)}
                    />
                ) : (
                    <>
                        <div className="ticker-arrow-shadow ticker-arrow-shadow-left" aria-hidden />
                        <div className="ticker-arrow-shadow ticker-arrow-shadow-right" aria-hidden />
                        <Carousel
                            className="ticker-carousel"
                            opts={{ loop: true, align: 'start', watchDrag: false }}
                            setApi={setApi}>
                            <CarouselContent className="ml-0 h-full">
                                {components.map((component, index) => (
                                    <CarouselItem key={component.id} className="h-full basis-full pl-0">
                                        <TickerSlide
                                            component={component}
                                            isActive={selectedIndex === index}
                                            isPaused={isPaused}
                                            isSingleSlide={false}
                                            onCycleComplete={handleCycleComplete}
                                            onMarqueePause={() => setIsPaused(true)}
                                            onMarqueeResume={() => setIsPaused(false)}
                                        />
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                            <CarouselPrevious variant="ghost" size="icon" className="ticker-arrow ticker-arrow-left" />
                            <CarouselNext variant="ghost" size="icon" className="ticker-arrow ticker-arrow-right" />
                        </Carousel>
                    </>
                )}
            </div>
        </aside>
    );
}
